import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BookOpen, X } from "lucide-react";
import { useServices } from "../hooks/useServices";
import { api } from "../lib/api";
import { ServiceImage } from "../components/ServiceImage";

export default function Services() {
  const SERVICES = useServices();
  const [articlesByService, setArticlesByService] = useState({});
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/blog").then((r) => {
      if (!alive) return;
      const map = {};
      (r.data || []).forEach((p) => {
        if (p.service_slug) {
          if (!map[p.service_slug]) map[p.service_slug] = [];
          map[p.service_slug].push(p);
        }
      });
      setArticlesByService(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div>
      <section className="bg-soft-cream">
        <div className="max-w-7xl mx-auto container-x py-16">
          <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Servicii</p>
          <h1 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight">Servicii stomatologice complete</h1>
          <p className="mt-5 text-brand-navy/75 text-lg max-w-3xl leading-relaxed font-light">
            Oferim servicii stomatologice pentru adulți și copii — profilaxie și igienizare, tratamente odontale, profilaxie parodontală, reabilitare orală cu proteze, tratamente de canal radicular, ortodonție (în colaborare cu medicul ortodont) și estetică dentară, în conformitate cu cele mai bune practici medicale actuale.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-white border border-brand-beige px-4 py-2 rounded-full text-brand-navy text-sm font-semibold">
            <ShieldCheck className="w-4 h-4 text-brand-green" /> Relație contractuală cu Casa de Asigurări de Sănătate Cluj
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto container-x py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-page-grid">
          {SERVICES.map((s) => {
            const related = articlesByService[s.slug] || [];
            return (
              <article key={s.slug} className="group bg-white rounded-2xl overflow-hidden border border-brand-beige shadow-sm hover:shadow-soft hover:-translate-y-1 transition-all flex flex-col" data-testid={`service-page-card-${s.slug}`}>
                <ServiceImage service={s} aspect="aspect-[4/3]" />
                <div className="p-7 flex flex-col flex-1">
                  <h2 className="font-display font-bold text-brand-navy text-xl leading-tight">{s.title}</h2>
                  <p className="mt-3 text-sm text-brand-navy/75 leading-relaxed">{s.short}</p>

                  {s.gallery && s.gallery.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-brand-beige">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-brand-navy/60 font-semibold mb-3">Detalii suplimentare</p>
                      <div className="grid grid-cols-2 gap-3" data-testid={`service-gallery-${s.slug}`}>
                        {s.gallery.map((g, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setLightbox(g)}
                            className="group/thumb rounded-xl overflow-hidden bg-brand-cream border border-brand-beige hover:border-brand-red hover:shadow-md transition-all text-left"
                            data-testid={`service-gallery-thumb-${s.slug}-${idx}`}
                            aria-label={`Vezi mai mult: ${g.caption}`}
                          >
                            <div className="aspect-[3/4] overflow-hidden bg-white">
                              <img src={g.src} alt={g.alt} className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-500" draggable={false} onContextMenu={(e)=>e.preventDefault()} />
                            </div>
                            <span className="block px-3 py-2 text-[11px] font-semibold text-brand-navy leading-tight">{g.caption}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {related.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-brand-beige">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-brand-navy/60 font-semibold flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" /> Articole legate
                      </p>
                      <ul className="mt-2 space-y-1.5">
                        {related.map((a) => (
                          <li key={a.id}>
                            <Link
                              to={`/articole/${a.slug}`}
                              className="text-sm text-brand-navy hover:text-brand-red font-medium leading-snug inline-flex items-start gap-1.5"
                              data-testid={`service-article-link-${a.slug}`}
                            >
                              <ArrowRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand-red" />
                              <span>{a.title}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <Link
                    to={`/programare?serviciu=${encodeURIComponent(s.title)}`}
                    className="mt-auto pt-5 inline-flex items-center gap-2 text-brand-red font-semibold text-sm group-hover:gap-3 transition-all"
                    data-testid={`book-${s.slug}`}
                  >
                    Programează <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12 bg-brand-cream rounded-3xl p-8 text-center">
          <p className="text-brand-navy/85">
            <strong>Tarifele</strong> sunt comunicate în cabinet, în funcție de complexitatea fiecărui caz. La consultația inițială primiți un plan de tratament detaliat și un deviz transparent <em>înainte</em> de începerea oricărei intervenții.
          </p>
        </div>
      </section>

      {/* Lightbox modal */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 no-img-drag"
          onClick={() => setLightbox(null)}
          data-testid="service-gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/95 text-brand-navy flex items-center justify-center shadow-lg hover:bg-white transition-colors"
            aria-label="Închide"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[90vh] w-auto h-auto rounded-2xl shadow-2xl bg-white object-contain"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
          />
        </div>
      )}
    </div>
  );
}
